import React, { useMemo, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';

interface ADORepository {
  id: string;
  name: string;
  webUrl?: string;
  remoteUrl?: string;
}

export const ADORepositoryPicker = ({
  onChange,
  errors,
  rawErrors,
  required,
  help,
  disabled,
  schema: { title = 'ADO Repositories', description },
  formData,
  uiSchema: _uiSchema,
  formContext,
}: FieldExtensionComponentProps<string[]>) => {
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Get organization and project from formContext (the full form data)
  const organization = formContext?.formData?.ADOOrganization || '';
  const project = formContext?.formData?.ADOProject || '';

  const backendUrl = config.getString('backend.baseUrl');
  const apiUrl = `${backendUrl}/api/ado-repositories-lister/repositories`;

  const { value, loading, error } = useAsync(async (): Promise<ADORepository[]> => {
    try {
      if (!organization || !project) {
        return [];
      }

      console.log(
        `ADORepositoryPicker: Fetching repositories for org: ${organization}, project: ${project}`,
      );

      const params = new URLSearchParams({
        organization,
        project,
      });
      const urlWithParams = `${apiUrl}?${params.toString()}`;

      const res = await fetchApi.fetch(urlWithParams, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errorText}`);
      }

      const data: ADORepository[] = await res.json();
      console.log('ADORepositoryPicker: Fetched repositories:', data);

      return data;
    } catch (err) {
      console.error('ADORepositoryPicker: Error fetching repositories:', err);
      throw err;
    }
  }, [organization, project]);

  const repositoryOptions = useMemo(() => {
    if (!value) return [];
    return value.map(repo => repo.name);
  }, [value]);

  const selectedRepos = useMemo(() => {
    return (formData && Array.isArray(formData) ? formData : []) as string[];
  }, [formData]);

  // Initialize with all repos on first load
  React.useEffect(() => {
    if (!hasInitialized && repositoryOptions.length > 0 && selectedRepos.length === 0) {
      console.log('ADORepositoryPicker: Initializing with all repos:', repositoryOptions);
      onChange(repositoryOptions);
      setHasInitialized(true);
    }
  }, [repositoryOptions, selectedRepos, hasInitialized, onChange]);

  const handleChange = (_event: any, newValue: any) => {
    // If user is selecting a single repo (going from multiple to one), clear others
    if (newValue.length === 1 && selectedRepos.length > 1) {
      onChange(newValue);
    } else {
      onChange(newValue);
    }
  };

  const handleSelectAll = () => {
    onChange(repositoryOptions);
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  if (!organization || !project) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <FormControl fullWidth disabled>
          <Autocomplete
            multiple
            options={[]}
            disabled
            renderInput={(params) => (
              <TextField {...params} label={title} />
            )}
          />
        </FormControl>
      </ScaffolderField>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <FormControl fullWidth>
          <Autocomplete
            multiple
            options={[]}
            loading
            disabled
            renderInput={(params) => (
              <TextField
                {...params}
                label={title}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      <CircularProgress color="inherit" size={20} />
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControl>
      </ScaffolderField>
    );
  }

  // Show error state
  if (error) {
    console.error('ADORepositoryPicker: Error state:', error);
    return (
      <ScaffolderField
        rawErrors={[
          ...(rawErrors || []),
          `Failed to load repositories: ${error.message}`,
        ]}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <Alert severity="error">
          Failed to load ADO repositories: {error.message}
        </Alert>
      </ScaffolderField>
    );
  }

  // Show dropdown with multi-select
  return (
    <ScaffolderField
      rawErrors={rawErrors}
      errors={errors}
      required={required}
      help={help}
      rawDescription={description}
      disabled={disabled}
    >
      <Box display="flex" flexDirection="column">
        <FormControl fullWidth error={rawErrors && rawErrors.length > 0}>
          <Autocomplete
            multiple
            options={repositoryOptions}
            value={selectedRepos}
            onChange={handleChange}
            disabled={disabled || repositoryOptions.length === 0}
            filterOptions={(options, state) =>
              options.filter(option =>
                option.toLowerCase().includes(state.inputValue.toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={title}
                placeholder="Search or select repositories..."
              />
            )}
            noOptionsText={
              repositoryOptions.length === 0
                ? `No ADO repositories found in organization ${organization} project ${project}`
                : 'No matching repositories'
            }
          />
          {help && <FormHelperText>{help}</FormHelperText>}
        </FormControl>
        {repositoryOptions.length > 0 && (
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              disabled={disabled || selectedRepos.length === repositoryOptions.length}
            >
              Select All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDeselectAll}
              disabled={disabled || selectedRepos.length === 0}
            >
              Deselect All
            </Button>
          </Box>
        )}
      </Box>
    </ScaffolderField>
  );
};

